import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button, Tooltip, Table, Card, Icon, Badge, Row, Col } from 'antd';

const locationStyles = `
@keyframes loc-beat { 0%,100%{transform:scale(1)} 50%{transform:scale(1.22)} }
@keyframes loc-float { 0%,100%{transform:translateY(0px)} 50%{transform:translateY(-4px)} }
@keyframes loc-spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
@keyframes loc-shake { 0%,100%{transform:rotate(0deg)} 20%{transform:rotate(-14deg)} 40%{transform:rotate(14deg)} 60%{transform:rotate(-8deg)} 80%{transform:rotate(8deg)} }
@keyframes loc-glow-ring { 0%,100%{box-shadow:0 0 0 0 var(--gc,rgba(24,144,255,.35))} 50%{box-shadow:0 0 0 8px rgba(0,0,0,0)} }

.loc-float { animation: loc-float 2.8s ease-in-out infinite; }
.loc-beat { animation: loc-beat 1.6s ease-in-out infinite; }
.loc-spin { animation: loc-spin 7s linear infinite; }
.loc-shake { animation: loc-shake 2.6s ease-in-out infinite; }
.loc-glow { animation: loc-glow-ring 2.4s ease-in-out infinite; }

/* ── Row stripe ── */
.loc-row-stripe td { background:#f7f9ff!important; }

/* ── Table card header ── */
.loc-card-header { padding:14px 20px 12px; border-bottom:1px solid #f0f5ff; display:flex; align-items:center; gap:8px; }

/* ── Table overrides ── */
.loc-table .ant-table-thead > tr > th { background:#f0f7ff!important; font-size:11px!important; font-weight:700!important; color:#1890ff!important; letter-spacing:1px!important; text-transform:uppercase!important; border-bottom:2px solid #d6e8ff!important; white-space:nowrap; }
.loc-table .ant-table-tbody > tr > td { font-size:12px!important; color:#262626!important; font-weight:500!important; border-color:#e6f0ff!important; padding:8px 12px!important; }
.loc-table .ant-table-bordered .ant-table-thead > tr > th,
.loc-table .ant-table-bordered .ant-table-tbody > tr > td { border-color:#e6f0ff!important; }
.loc-table .ant-table-tbody > tr:hover > td { background:#e6f7ff!important; }
.loc-table .ant-pagination { padding:8px 16px!important; }

/* ── Action buttons ── */
.loc-action-btn { width:28px!important; height:28px!important; min-width:28px!important; padding:0!important; border-radius:7px!important; display:inline-flex!important; align-items:center!important; justify-content:center!important; font-size:13px!important; border:1.5px solid #d6e8ff!important; background:#e6f7ff!important; color:#1890ff!important; transition:all .18s!important; margin:0 2px!important; }
.loc-action-btn:hover { transform:translateY(-1px); box-shadow:0 3px 8px rgba(24,144,255,.25)!important; }
.loc-action-btn.delete:hover { background:#ff4d4f!important; color:#fff!important; border-color:#ff4d4f!important; }
.loc-action-btn.edit:hover { background:#1890ff!important; color:#fff!important; border-color:#1890ff!important; }

/* ── Status badges ── */
.loc-status-yes { background:#f6ffed; color:#52c41a; border:1px solid #b7eb8f; padding:2px 12px; border-radius:20px; font-size:11px; font-weight:600; display:inline-block; }
.loc-status-no { background:#fff1f0; color:#ff4d4f; border:1px solid #ffa39e; padding:2px 12px; border-radius:20px; font-size:11px; font-weight:600; display:inline-block; }

/* ── Map position chip ── */
.loc-map-chip { background:#e6f7ff; padding:2px 12px; border-radius:20px; font-weight:600; color:#1890ff; font-family:monospace; font-size:11px; display:inline-block; }

/* ── Size chip ── */
.loc-size-chip { background:#f9f0ff; padding:2px 12px; border-radius:20px; font-weight:600; color:#722ed1; font-size:11px; display:inline-block; }
`;

const LocationTable = ({ locations, openModal, deleteRecord }) => {
  // Calculate location stats
  const stats = useMemo(() => {
    if (!locations || locations.length === 0) {
      return { totalLocations: 0, shownOnMap: 0, totalItems: 0, avgItemsPerLocation: 0 };
    }
    
    const shownOnMap = locations.filter(loc => loc.showOnMap).length;
    const totalItems = locations.reduce((sum, loc) => sum + (loc.itemsCount || 0), 0);
    const avgItemsPerLocation = (totalItems / locations.length).toFixed(1);
    
    return {
      totalLocations: locations.length,
      shownOnMap,
      totalItems,
      avgItemsPerLocation
    };
  }, [locations]);

  const cardStyle = {
    borderRadius: 10,
    boxShadow: '0 2px 12px rgba(24,144,255,0.08)',
    border: '1px solid #e6f0ff',
    background: '#fff',
    height: '100%',
  };

  // Summary cards data
  const summaryCards = [
    {
      title: 'Total Locations',
      value: stats.totalLocations,
      icon: 'environment',
      color: '#1890ff',
      bg: '#e6f7ff',
      anim: 'loc-float',
      glow: 'rgba(24,144,255,0.3)',
    },
    {
      title: 'Shown on Map',
      value: stats.shownOnMap,
      icon: 'global',
      color: '#52c41a',
      bg: '#f6ffed',
      anim: 'loc-beat',
      glow: 'rgba(82,196,26,0.3)',
    },
    {
      title: 'Total Items',
      value: stats.totalItems,
      icon: 'inbox',
      color: '#fa8c16',
      bg: '#fff7e6',
      anim: 'loc-spin',
      glow: 'rgba(250,140,22,0.3)',
    },
    {
      title: 'Avg Items/Location',
      value: stats.avgItemsPerLocation,
      icon: 'bar-chart',
      color: '#722ed1',
      bg: '#f9f0ff',
      anim: 'loc-shake',
      glow: 'rgba(114,46,209,0.3)',
    },
  ];

  const columns = [
    {
      key: 'deleteBtn',
      width: 46,
      align: 'center',
      render: record => (
        <Tooltip title="Delete location" placement="right">
          <Button 
            icon="delete" 
            className="loc-action-btn delete" 
            onClick={() => deleteRecord(record.id)}
            size="small"
          />
        </Tooltip>
      ),
    },
    {
      key: 'editBtn',
      width: 46,
      align: 'center',
      render: record => (
        <Tooltip title="Edit location" placement="right">
          <Button 
            icon="edit" 
            className="loc-action-btn edit" 
            onClick={() => openModal(record)}
            size="small"
          />
        </Tooltip>
      ),
    },
    {
      key: 'label',
      title: 'LOCATION',
      dataIndex: 'label',
      align: 'center',
      sorter: (a, b) => {
        const A = a.label || '';
        const B = b.label || '';
        return A.localeCompare(B);
      },
      render: val => <span style={{ fontWeight: 600, color: '#262626' }}>{val}</span>
    },
    {
      key: 'showOnMap',
      title: 'SHOW ON MAP',
      render: record => (
        <span className={record.showOnMap ? 'loc-status-yes' : 'loc-status-no'}>
          {record.showOnMap ? 'YES' : 'NO'}
        </span>
      ),
      align: 'center',
      sorter: (a, b) => {
        const A = a.showOnMap || '';
        const B = b.showOnMap || '';
        return A.localeCompare(B);
      },
    },
    {
      key: 'itemsCount',
      title: 'ITEM/S COUNT',
      dataIndex: 'itemsCount',
      align: 'center',
      sorter: (a, b) => (a.itemsCount || 0) - (b.itemsCount || 0),
      render: val => (
        <span style={{ 
          background: '#e6f7ff', 
          padding: '2px 10px', 
          borderRadius: 12, 
          fontWeight: 600, 
          color: '#1890ff',
          display: 'inline-block'
        }}>
          {val || 0}
        </span>
      )
    },
    {
      key: 'map',
      title: 'MAP POSITION (X, Y)',
      align: 'center',
      render: record => {
        const xPos = record.x_position !== undefined && record.x_position !== null ? record.x_position.toFixed(0) : 0;
        const yPos = record.y_position !== undefined && record.y_position !== null ? record.y_position.toFixed(0) : 0;
        return (
          <span className="loc-map-chip">
            <Icon type="environment" style={{ fontSize: 10, marginRight: 4 }} />
            {xPos} / {yPos}
          </span>
        );
      },
    },
    {
      key: 'size',
      title: 'SIZE ON MAP (W x H)',
      render: record => {
        const width = record.width || 0;
        const height = record.height || 0;
        return (
          <span className="loc-size-chip">
            <Icon type="border" style={{ fontSize: 10, marginRight: 4 }} />
            {width} x {height}
          </span>
        );
      },
      align: 'center',
    },
  ];

  return (
    <Fragment>
      <style>{locationStyles}</style>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        {summaryCards.map((card, idx) => (
          <Col key={idx} xs={12} sm={6}>
            <Card style={cardStyle} bodyStyle={{ padding: '18px 20px' }} bordered={false}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div
                  className={`loc-glow ${card.anim}`}
                  style={{
                    width: 46,
                    height: 46,
                    borderRadius: 12,
                    background: card.bg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    '--gc': card.glow,
                  }}
                >
                  <Icon type={card.icon} style={{ fontSize: 20, color: card.color }} />
                </div>
                <div>
                  <div style={{
                    fontSize: 11,
                    color: '#8c8c8c',
                    fontWeight: 500,
                    letterSpacing: '0.3px',
                    whiteSpace: 'nowrap',
                  }}>
                    {card.title}
                  </div>
                  <div style={{
                    fontSize: 24,
                    fontWeight: 700,
                    color: card.color,
                    lineHeight: 1.2,
                  }}>
                    {card.value}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Main Table Card */}
      <Card
        bordered={false}
        bodyStyle={{ padding: 0 }}
        style={{
          borderRadius: 10,
          boxShadow: '0 2px 12px rgba(24,144,255,0.07)',
          border: '1px solid #e6f0ff',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div className="loc-card-header">
          <div
            className="loc-glow"
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: '#e6f7ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '--gc': 'rgba(24,144,255,0.3)',
            }}
          >
            <Icon
              type="environment"
              style={{ color: '#1890ff', fontSize: 14, animation: 'loc-beat 1.8s ease-in-out infinite' }}
            />
          </div>
          <span style={{ fontWeight: 700, fontSize: 13, color: '#262626', letterSpacing: '.5px' }}>
            LOCATIONS
          </span>
          <Badge
            count={stats.totalLocations}
            style={{ backgroundColor: '#1890ff', fontSize: 11, fontWeight: 600 }}
            overflowCount={99999}
          />
        </div>

        {/* Table */}
        <Table
          className="loc-table"
          dataSource={locations}
          columns={columns}
          rowKey="id"
          size="small"
          bordered={true}
          bodyStyle={{
            overflowX: 'auto',
          }}
          rowClassName={(_, idx) => (idx % 2 === 0 ? '' : 'loc-row-stripe')}
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ['5', '10', '20', '50', '100'],
            defaultPageSize: 10,
            showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} locations`,
            style: { padding: '8px 16px', marginBottom: 0 },
          }}
        />
      </Card>
    </Fragment>
  );
};

LocationTable.propTypes = {
  locations: PropTypes.arrayOf(PropTypes.object).isRequired,
  openModal: PropTypes.func.isRequired,
  deleteRecord: PropTypes.func.isRequired,
};

export default LocationTable;